// Trang con Thân (UI_SPEC §18.6): Thiết lập (trợ năng) + xuất / nhập save.
import type { JSX } from 'preact';
import { useState } from 'preact/hooks';
import { t } from '@i18n/t';
import { settings, toggleSetting, type BoolSetting } from '@state/modules/settings';
import { askConfirm } from '@state/modules/ui';
import { logLine } from '@state/modules/log';
import { adoptDoc, currentStore, flush, saveReadOnly } from '@state/persistence';
import { TextButton } from '../kit/TextButton';
import { lastReturnSummary } from '@state/modules/returnSummary';
import { ReturnBody } from '../shell/ReturnScreen';

const TOGGLES: readonly BoolSetting[] = [
  'reduceMotion',
  'horrorFx',
  'screenShake',
  'reduceFlash',
  'highContrast',
];

export function SelfPage(): JSX.Element {
  const [text, setText] = useState('');
  const [error, setError] = useState(false);
  const s = settings.value;

  const doExport = async (): Promise<void> => {
    const store = currentStore();
    if (!store) return;
    await flush();
    setText(await store.exportString());
    setError(false);
    logLine('system', 'log.system.saveExported');
  };

  const doExportCorrupt = async (): Promise<void> => {
    const store = currentStore();
    if (!store) return;
    const keys = await store.quarantinedKeys();
    const last = keys[keys.length - 1];
    if (last) setText((await store.readQuarantined(last)) ?? '');
  };

  const doImport = async (): Promise<void> => {
    const store = currentStore();
    if (!store) return;
    try {
      const doc = await store.importString(text);
      setError(false);
      askConfirm({
        textKey: 'ui.save.askOverwrite',
        confirmKey: 'ui.save.overwrite',
        onConfirm: () => void adoptDoc(doc),
      });
    } catch {
      setError(true);
    }
  };

  const ret = lastReturnSummary.value;
  return (
    <>
      {ret && (
        <>
          <div class="ht-section-label">{t('ui.self.return')}</div>
          <ReturnBody s={ret} compact />
        </>
      )}
      <div class="ht-section-label">{t('ui.self.settings')}</div>
      {TOGGLES.map((k) => (
        <div class="ht-statline" key={k}>
          <span>{t(`ui.settings.${k}`)}</span>
          <TextButton variant={s[k] ? 'goto' : 'ghost'} id={`setting-${k}`} onClick={() => toggleSetting(k)}>
            {t(s[k] ? 'ui.settings.on' : 'ui.settings.off')}
          </TextButton>
        </div>
      ))}
      <div class="ht-section-label">{t('ui.self.save')}</div>
      {saveReadOnly.value && <p class="ht-reason">{t('ui.save.readOnly')}</p>}
      <textarea
        class="ht-textarea"
        data-id="save-field"
        aria-label={t('ui.save.field')}
        value={text}
        onInput={(e) => setText((e.target as HTMLTextAreaElement).value)}
      />
      {error && <p class="ht-reason">{t('ui.save.importError')}</p>}
      <div class="ht-actions">
        <TextButton id="save-import" onClick={() => void doImport()} disabled={saveReadOnly.value}>
          {t('ui.save.import')}
        </TextButton>
        <TextButton variant="ghost" id="save-export" onClick={() => void doExport()}>
          {t('ui.save.export')}
        </TextButton>
        <TextButton variant="ghost" id="save-export-corrupt" onClick={() => void doExportCorrupt()}>
          {t('ui.save.exportCorrupt')}
        </TextButton>
      </div>
    </>
  );
}
